import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../ui/LatexRenderer';
export function RatioTableRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.interactiveConfig || {};
  const headers = config.headers || [];
  const rows = config.rows || []; // The FULL correct table data
  const hiddenCells = config.hiddenCells || []; // Array of { row: number, col: number }
  const prevValidateRef = useRef(false);
  // State for user inputs: key is "row-col", value is user string
  const [inputs, setInputs] = useState<Record<string, string>>({});
  // Reset state when question changes
  useEffect(() => {
    setInputs({});
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  // Helper to generate key
  const getKey = (r: number, c: number) => `${r}-${c}`;
  // Check if a cell is hidden
  const isHidden = (r: number, c: number) => {
    return hiddenCells.some((cell: any) => cell.row === r && cell.col === c);
  };
  // Handle input change
  const handleChange = (r: number, c: number, value: string) => {
    setInputs(prev => ({
      ...prev,
      [getKey(r, c)]: value
    }));
  };
  // Report readiness
  useEffect(() => {
    // Ready if all hidden cells have a value
    const allFilled = hiddenCells.every((cell: any) => {
      const val = inputs[getKey(cell.row, cell.col)];
      return val && val.trim().length > 0;
    });
    onReadyChange?.(allFilled);
  }, [inputs, hiddenCells, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      // Return JSON string of inputs for validation
      onAnswer(JSON.stringify(inputs));
    }
  }, [shouldValidate, inputs, onAnswer]);
  return <div className="w-full max-w-3xl mx-auto" data-id="element-4776">
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-4777">
        <div className="overflow-x-auto" data-id="element-4778">
          <table className="w-full border-collapse text-left" data-id="element-4779">
            <thead data-id="element-4780">
              <tr className="bg-slate-50 border-b-2 border-slate-200" data-id="element-4781">
                {headers.map((header: string, i: number) => <th key={i} className="p-4 font-bold text-slate-700 min-w-[120px]" data-id="element-4782">

                    <LatexRenderer content={header} data-id="element-4783" />
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-4784">
              {rows.map((row: string[], rowIndex: number) => <tr key={rowIndex} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-id="element-4785">

                  {row.map((cellContent: string, colIndex: number) => {
                const hidden = isHidden(rowIndex, colIndex);
                const key = getKey(rowIndex, colIndex);
                return <td key={colIndex} className="p-3" data-id="element-4786">
                        {hidden ? <input type="text" value={inputs[key] || ''} onChange={e => handleChange(rowIndex, colIndex, e.target.value)} className="w-full min-w-[80px] p-2 text-center font-medium text-indigo-700 bg-indigo-50 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all placeholder:text-indigo-200" placeholder="?" autoComplete="off" data-id="element-4787" /> : <span className="text-slate-700 font-medium px-2" data-id="element-4788">
                            <LatexRenderer content={cellContent} data-id="element-4789" />
                          </span>}
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-slate-400 text-sm mt-4" data-id="element-4790">
        Vul de ontbrekende getallen in de verhoudingstabel in
      </p>
    </div>;
}